import "@/App.css";
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { captureUtm } from "@/lib/utm";
import ProtectedRoute from "@/components/ProtectedRoute";
import ConsentBanner from "@/components/ConsentBanner";
import Landing from "@/pages/Landing";
import ServicePage from "@/pages/seo/ServicePage";
import ServiceEnquiryPage from "@/pages/seo/ServiceEnquiryPage";
import AboutFedari from "@/pages/seo/AboutFedari";
import Contact from "@/pages/seo/Contact";

import Partners from "@/pages/seo/Partners";
import SampleReport from "@/pages/seo/SampleReport";
import InspectionTools from "@/pages/seo/InspectionTools";
import CommunityPage from "@/pages/seo/CommunityPage";
import CommunitiesHub from "@/pages/seo/CommunitiesHub";
import GuidesHub from "@/pages/seo/GuidesHub";
import GuidePage from "@/pages/seo/GuidePage";
import NotFound from "@/pages/seo/NotFound";
import { SERVICE_SLUGS } from "@/data/servicePages";
import { SERVICE_ENQUIRY_SLUGS } from "@/data/serviceEnquiryPages";
import { COMMUNITY_SLUGS } from "@/data/communityPages";
import { GUIDE_SLUGS } from "@/data/guidePages";
import { AR_GUIDE_SLUGS } from "@/data/slugs";
import { LEGAL_CONTENT_SLUGS } from "@/data/slugs";

// Private / transactional routes are code-split so their (heavy) JS — the admin
// dashboard, charts, etc. — is never shipped to public SEO pages.
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const Manage = lazy(() => import("@/pages/Manage"));

// Low-traffic public pages are also code-split: the bilingual Terms (large legal
// copy), the markdown legal pages (react-markdown) and the Arabic guides. This
// keeps ~350KB out of the main bundle while these routes remain prerendered
// (the prerenderer waits for real content, so snapshots stay complete).
const LegalMarkdownPage = lazy(() => import("@/pages/legal/LegalMarkdownPage"));
const TermsAndConditions = lazy(() => import("@/pages/legal/TermsAndConditions"));
const TermsAndConditionsAr = lazy(() => import("@/pages/legal/TermsAndConditionsAr"));
const GuidesHubAr = lazy(() => import("@/pages/seo/GuidesHubAr"));
const GuidePageAr = lazy(() => import("@/pages/seo/GuidePageAr"));

// Keeps private/transactional routes out of search indexes (React 19 hoists
// this <meta> into <head>). Belt-and-braces alongside the X-Robots-Tag header.
function NoIndex() {
  return <meta name="robots" content="noindex,nofollow,noarchive" />;
}

// Each lazy element gets its OWN Suspense. Public (eager) SEO routes are NOT
// wrapped in Suspense, so prerendered pages hydrate without a Suspense
// boundary mismatch (React #418).
const Lazy = (el) => <Suspense fallback={null}>{el}</Suspense>;

// Start every page at the top after menu/route navigation — unless the URL
// targets an in-page anchor (e.g. /#pricing), in which case scroll to it.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) { el.scrollIntoView({ behavior: "instant", block: "start" }); return; }
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

function App() {
  useEffect(() => { captureUtm(); }, []);
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Landing />} />

            {/* SEO content pages */}
            {SERVICE_SLUGS.map((slug) => (
              <Route key={slug} path={`/${slug}`} element={<ServicePage slug={slug} />} />
            ))}
            {SERVICE_ENQUIRY_SLUGS.map((slug) => (
              <Route key={slug} path={`/${slug}`} element={<ServiceEnquiryPage slug={slug} />} />
            ))}
            {LEGAL_CONTENT_SLUGS.map((slug) => (
              <Route key={slug} path={`/${slug}`} element={Lazy(<LegalMarkdownPage slug={slug} />)} />
            ))}
            {/* Full bilingual Terms & Conditions (v1.1) */}
            <Route path="/terms-and-conditions" element={Lazy(<TermsAndConditions />)} />
            <Route path="/ar/terms-and-conditions" element={Lazy(<TermsAndConditionsAr />)} />
            <Route path="/about-fedari" element={<AboutFedari />} />
            <Route path="/about-us" element={<Navigate to="/about-fedari" replace />} />
            <Route path="/about" element={<Navigate to="/about-fedari" replace />} />
            <Route path="/contact" element={<Contact />} />

            <Route path="/partners" element={<Partners />} />
            <Route path="/sample-snagging-report" element={<SampleReport />} />
            <Route path="/inspection-tools" element={<InspectionTools />} />

            {/* Community SEO hub + per-community landing pages */}
            <Route path="/property-snagging-dubai-communities" element={<CommunitiesHub />} />
            {COMMUNITY_SLUGS.map((slug) => (
              <Route key={slug} path={`/${slug}`} element={<CommunityPage slug={slug} />} />
            ))}

            {/* Educational guides cluster */}
            <Route path="/guides" element={<GuidesHub />} />
            {GUIDE_SLUGS.map((slug) => (
              <Route key={slug} path={`/guides/${slug}`} element={<GuidePage slug={slug} />} />
            ))}

            {/* Arabic guides (top-intent guides, RTL + hreflang) */}
            <Route path="/ar/guides" element={Lazy(<GuidesHubAr />)} />
            {AR_GUIDE_SLUGS.map((slug) => (
              <Route key={slug} path={`/ar/guides/${slug}`} element={Lazy(<GuidePageAr slug={slug} />)} />
            ))}

            {/* Private / transactional — never indexed, code-split */}
            <Route path="/payment/success" element={<><NoIndex />{Lazy(<PaymentSuccess />)}</>} />
            <Route path="/payment/cancel" element={<><NoIndex />{Lazy(<PaymentCancel />)}</>} />
            <Route path="/manage/:token" element={<><NoIndex />{Lazy(<Manage />)}</>} />
            <Route path="/admin/login" element={<><NoIndex />{Lazy(<AdminLogin />)}</>} />
            <Route path="/admin" element={<><NoIndex />{Lazy(<ProtectedRoute><AdminDashboard /></ProtectedRoute>)}</>} />

            {/* 404 */}
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <ConsentBanner />
        </BrowserRouter>
        <Toaster position="bottom-right" theme="light" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
